import { ChangeDetectionStrategy, Component, input, output } from '@angular/core';
import { TableColumn } from '../models/table-column';
import { ReactiveFormsModule } from '@angular/forms';
import { TableHeaderRowComponent } from '../table-header-row/table-header-row.component';
import { OdataFilterExpression, ODataFilterGroup, OdataQueryOptions, SortExpression } from '../../../../core';
import { BehaviorSubject, combineLatest, debounceTime, Observable } from 'rxjs';
import { ButtonType } from '../../enum/button-type.enum';
import { TableOptionsRowComponent } from '../table-options-row/table-options-row.component';
import { TableColumnValuePipe } from '../pipes/TableColumnValuePipe';
import { AsyncPipe, NgComponentOutlet } from '@angular/common';
import { TablePaginationComponent } from '../table-pagination/table-pagination.component';
import { LoadingShimmerComponent } from '../../loading-shimmer/loading-shimmer.component';
import { RepeatDirective } from '../../../directives/repeat.directive';
import { CellComponentRowDataPipe } from '../pipes/CellComponentRowDataPipe';
import { TableFilterGroup } from '../models/table-filter';
import { TableNoResultsComponent } from '../table-no-results/table-no-results.component';
import { TooltipDirective } from '../../../directives/tooltip.directive';
import { OverflowDirective } from '../../../directives/overflow.directive';

@Component({
  selector: 'lib-table',
  imports: [
    TableColumnValuePipe,
    CellComponentRowDataPipe,
    ReactiveFormsModule,
    TableHeaderRowComponent,
    TableOptionsRowComponent,
    AsyncPipe,
    TablePaginationComponent,
    LoadingShimmerComponent,
    RepeatDirective,
    NgComponentOutlet,
    TableNoResultsComponent,
    TooltipDirective,
    OverflowDirective,
  ],
  templateUrl: './table.component.html',
  styleUrl: './table.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export class TableComponent<T> {
  totalRowCount = input.required<number | null>();
  data = input<T[] | null>(null);
  columns = input.required<TableColumn<T>[]>();
  defaultSortColumn = input.required<TableColumn<T>>();
  defaultSortAsc = input.required<boolean>();
  tableFilterGroups = input<TableFilterGroup[] | null>(null);
  loading = input<boolean>(false);
  exportCSVDataFn = input<() => Observable<T[]>>();
  rowClicked = output<T>();
  odataQueryOptions = output<OdataQueryOptions>();

  currentTop = new BehaviorSubject<number | null>(null);
  currentSkip = new BehaviorSubject<number | null>(null);
  currentSortExpression = new BehaviorSubject<SortExpression | null>(null);
  currentSearchFilterExpressions = new BehaviorSubject<(OdataFilterExpression | ODataFilterGroup)[] | null>(null);
  currentFilterGroups = new BehaviorSubject<ODataFilterGroup[] | null>(null);

  protected readonly ButtonType = ButtonType;
  private readonly debounceTimeMillis = 500;

  constructor() {
    combineLatest([
      this.currentSortExpression,
      this.currentSearchFilterExpressions,
      this.currentFilterGroups,
      this.currentTop,
      this.currentSkip,
    ])
      .pipe(debounceTime(this.debounceTimeMillis))
      .subscribe(([sortExpression, filterExpressions, filterGroups, top, skip]) => {
        const query = new OdataQueryOptions();
        query.orderBy = sortExpression ? [sortExpression] : [];
        query.filters = [...(filterExpressions ?? []), ...(filterGroups ?? [])];
        query.top = top ?? undefined;
        query.skip = skip ?? undefined;
        this.odataQueryOptions.emit(query);
      });
  }

  protected currentTopUpdated(rowCount: number | null): void {
    this.currentTop.next(rowCount);
  }

  protected sortExpressionUpdated(sortExpression: SortExpression | null): void {
    this.currentSortExpression.next(sortExpression);
  }

  protected searchFilterExpressionsUpdated(
    filterExpressions: (OdataFilterExpression | ODataFilterGroup)[] | null,
  ): void {
    this.currentSearchFilterExpressions.next(filterExpressions);
  }

  protected onTableFiltersUpdated(): void {
    const filterGroups =
      this.tableFilterGroups()?.flatMap(g => g.filters.map(f => f.buildODataFilterGroup()).filter(f => f !== null)) ??
      [];
    this.currentFilterGroups.next(filterGroups);
  }

  protected currentSkipUpdated(skip: number | null): void {
    this.currentSkip.next(skip);
  }

  protected clearColumnFilters() {
    this.columns().forEach(c => {
      c.clearFilter();
    });
  }

  protected tableCellClicked(column: TableColumn<T>, rowData: T, event: MouseEvent) {
    const targetElement = event.target as HTMLElement;
    if (column?.disableRowClickOnColumn === false && !targetElement.closest('.options-table-cell-clickable-icon')) {
      this.rowClicked.emit(rowData);
    }
  }
}
